import { v4 as uuidv4 } from 'uuid';
import logger from '@/utils/logger';

/**
 * Parses content that can be either a string (JSON) or an object
 * Returns the parsed object or the original content if parsing fails
 */
export const parseContent = (content: string | object | null | undefined): any => {
  if (!content) return null;

  if (typeof content === 'object') {
    return content;
  }

  if (typeof content === 'string') {
    try {
      return JSON.parse(content);
    } catch {
      return content;
    }
  }

  return content;
};

/**
 * Merges incremental JSON content for insight responses
 * Handles both string (JSON) and object formats, merging them incrementally
 */
export const mergeInsightContent = (existingContent: any, newContent: string | object | null | undefined): any => {
  if (!newContent) return existingContent;

  try {
    const existingParsed = parseContent(existingContent);
    const newParsed = parseContent(newContent);

    // Merge incremental JSON: existing content + new content
    if (existingParsed && typeof existingParsed === 'object' && typeof newParsed === 'object') {
      return { ...existingParsed, ...newParsed };
    }

    return newParsed || newContent;
  } catch (error) {
    logger('Failed to merge insight content:', error);
    return newContent;
  }
};

/**
 * Reassembles text chunks in order based on their sequence numbers
 * Returns the complete reassembled text
 */
export const reassembleTextChunks = (chunksMap: Map<number, string>): string => {
  const sortedSequences = Array.from(chunksMap.keys()).sort((a, b) => a - b);
  return sortedSequences.map(seq => chunksMap.get(seq) || '').join('');
};

/**
 * Validates if a response has the required fields for text response handling
 */
export const isValidTextResponse = (type: string | undefined, content: any, sequence: number | undefined): boolean => {
  return type === 'text' && sequence !== undefined && typeof content === 'string';
};

/**
 * Validates if a response has the required fields for insight response handling
 */
export const isValidInsightResponse = (type: string | undefined, content: any): boolean => {
  return type === 'insight' && !!content;
};

/**
 * Validates if message content is valid for sending
 */
export const isValidMessageContent = (content: string | null | undefined): boolean => {
  return !!content && content.trim().length > 0;
};

/**
 * Creates a user message object
 */
export const createUserMessage = (
  content: string,
  stationId: string | undefined,
  messageId?: string,
): {
  id: string;
  role: 'user';
  content: string;
  timestamp: Date;
  stationId: string | undefined;
  messageId?: string;
} => {
  return {
    id: messageId || uuidv4(),
    role: 'user',
    content: content.trim(),
    timestamp: new Date(),
    stationId,
  };
};

/**
 * Creates an assistant message placeholder for streaming responses
 */
export const createAssistantMessagePlaceholder = (
  messageId: number,
  stationId: string | undefined,
  messageIdString?: string,
): {
  id: string;
  role: 'assistant';
  content: string;
  timestamp: Date;
  stationId: string | undefined;
  isStreaming: boolean;
  messageId: string;
  isFromHistory: false;
} => {
  return {
    id: uuidv4(),
    role: 'assistant',
    content: '',
    timestamp: new Date(),
    stationId,
    isStreaming: true,
    messageId: messageIdString || messageId.toString(),
    isFromHistory: false,
  };
};
